import React, { useEffect, useRef, useState } from 'react';

const BOT_URL = 'https://t.me/ShadowSocks_bot';
const FORM_URL = 'https://forms.gle/mNnn1JR2wJWod2vF7';

const FEATURES = [
  {
    title: 'Современные протоколы',
    desc: 'Соединение держится там, где обычные VPN уже не проходят',
  },
  {
    title: 'Устойчивость к блокировкам',
    desc: 'Доступ остаётся, даже когда сети ужесточают фильтрацию',
  },
  {
    title: 'Приватность по умолчанию',
    desc: 'Трафик зашифрован: провайдер не видит, что вы открываете',
  },
];

// Знак shasys: заглавная S из Familjen Grotesk Regular (filled path, vault P2a)
const S_PATH =
  'M131.12 202.25L131.12 202.25Q117.18 202.25 106.64 198.40Q96.10 194.55 88.86 187.68Q81.62 180.80 77.67 171.63Q73.73 162.47 73.00 151.83L73.00 151.83L92.80 151.83Q93.35 160.27 97.75 168.06Q102.15 175.85 110.67 180.80Q119.20 185.75 132.03 185.75L132.03 185.75Q145.97 185.75 153.85 179.61Q161.73 173.47 161.73 162.83L161.73 162.83Q161.73 152.20 154.13 146.52Q146.52 140.83 134.97 136.98L134.97 136.98L121.22 132.40Q110.77 128.92 101.23 124.24Q91.70 119.57 85.65 111.87Q79.60 104.17 79.60 91.70L79.60 91.70Q79.60 79.60 85.47 71.17Q91.33 62.73 101.78 58.24Q112.23 53.75 125.98 53.75L125.98 53.75Q142.67 53.75 153.48 59.71Q164.30 65.67 169.80 75.38Q175.30 85.10 176.03 96.83L176.03 96.83L156.23 96.83Q155.68 90.05 152.47 84.00Q149.27 77.95 142.67 74.10Q136.07 70.25 125.25 70.25L125.25 70.25Q113.52 70.25 106.46 75.47Q99.40 80.70 99.40 89.87L99.40 89.87Q99.40 100.13 107.56 105.63Q115.72 111.13 127.08 114.80L127.08 114.80L141.02 119.38Q151.47 122.68 160.72 127.45Q169.98 132.22 175.76 140.01Q181.53 147.80 181.53 160.82L181.53 160.82Q181.53 180.62 167.78 191.43Q154.03 202.25 131.12 202.25Z';

const SMark = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 256 256"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d={S_PATH} />
  </svg>
);

// Один общий IntersectionObserver на все scroll-reveal цели; unobserve после показа.
const revealCallbacks = new WeakMap();
let revealObserver = null;

function registerReveal(el, onReveal) {
  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!el || typeof IntersectionObserver === 'undefined' || reduceMotion) {
    onReveal();
    return undefined;
  }

  if (!revealObserver) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const cb = revealCallbacks.get(entry.target);
          if (cb) cb();
          revealObserver.unobserve(entry.target);
          revealCallbacks.delete(entry.target);
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );
  }

  revealCallbacks.set(el, onReveal);
  revealObserver.observe(el);

  return () => {
    if (revealObserver) revealObserver.unobserve(el);
    revealCallbacks.delete(el);
  };
}

function Reveal({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => registerReveal(ref.current, () => setShown(true)), []);

  return (
    <Tag
      ref={ref}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={`reveal-on-scroll${shown ? ' is-visible' : ''}${
        className ? ` ${className}` : ''
      }`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

function App() {
  return (
    <main className="flex min-h-screen flex-col bg-ink font-sans text-fog">
      {/* Hero — on-load stagger */}
      <section className="flex min-h-screen flex-col items-center justify-center px-6 py-24 text-center">
        <h1 className="text-6xl font-normal leading-none tracking-tight motion-safe:animate-reveal motion-safe:opacity-0 sm:text-7xl lg:text-8xl">
          shasys
        </h1>
        <p className="mt-7 max-w-md text-lg leading-relaxed text-fog/70 [animation-delay:90ms] motion-safe:animate-reveal motion-safe:opacity-0 sm:text-xl">
          Приватный доступ в интернет, который просто работает
        </p>
        <p className="mt-4 max-w-md text-base leading-relaxed text-fog/55 [animation-delay:170ms] motion-safe:animate-reveal motion-safe:opacity-0 sm:text-lg">
          Обходит блокировки, шифрует трафик, работает на всех устройствах
        </p>
        <a
          href={BOT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-10 inline-flex items-center gap-2 text-[15px] text-fog transition-colors duration-300 [animation-delay:250ms] hover:text-accent motion-safe:animate-reveal motion-safe:opacity-0 sm:text-base"
        >
          Подключиться
          <span
            className="text-accent transition-transform duration-300 group-hover:translate-x-1"
            aria-hidden="true"
          >
            →
          </span>
        </a>
        <span className="mt-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-fog/40 [animation-delay:330ms] motion-safe:animate-reveal motion-safe:opacity-0">
          вход через Telegram
        </span>
      </section>

      {/* Доверие + цена + возможности — один плотный экран */}
      <section className="border-t border-fog/10 px-6 py-24 sm:py-32">
        <div className="mx-auto max-w-4xl">
          <Reveal as="div" className="text-center">
            <p className="mx-auto max-w-2xl text-xl leading-relaxed text-fog/90 sm:text-2xl">
              С 2018 года. Годы стабильной работы. Зрелый сервис, а не обещание.
            </p>
            <p className="mt-4 text-sm text-fog/40">От 260 ₽/мес</p>
          </Reveal>

          <div className="mt-16 border-t border-fog/10 pt-16 sm:mt-20 sm:pt-20">
            <ul className="grid grid-cols-1 divide-y divide-fog/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              {FEATURES.map((feature, i) => (
                <Reveal
                  as="li"
                  key={feature.title}
                  delay={i * 90}
                  className="p-8 text-center"
                >
                  <h3 className="text-lg font-medium text-fog sm:text-xl">
                    {feature.title}
                  </h3>
                  <p className="mx-auto mt-3 max-w-[26ch] text-sm leading-relaxed text-fog/55">
                    {feature.desc}
                  </p>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Footer — тихая строка-форма + lockup [S] shasys */}
      <footer className="border-t border-fog/10 px-6 py-16">
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-8 text-center">
          <p className="text-xs text-fog/40">
            Развиваем распределённую сеть
            <a
              href={FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline ml-2 inline-flex items-center gap-1 text-fog/55 transition-colors duration-300 hover:text-accent"
            >
              лист ожидания
              <span className="text-accent" aria-hidden="true">
                →
              </span>
            </a>
          </p>
          <Reveal as="div" className="flex items-center gap-2.5 text-fog/45">
            <SMark className="h-[18px] w-[18px]" />
            <span className="text-sm lowercase tracking-tight">shasys</span>
          </Reveal>
        </div>
      </footer>
    </main>
  );
}

export default App;
